import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Phone, LogOut, RefreshCw, MessageCircle, Lock } from "lucide-react";

const Admin = () => {
  const [token, setToken] = useState(() => localStorage.getItem("amyee_admin_token") || "");
  const [password, setPassword] = useState("");
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);

  const login = async (e) => {
    e.preventDefault();
    if (!password) return;
    setLoading(true);
    try {
      const res = await api.post("/admin/login", { password });
      const tk = res.data.token;
      localStorage.setItem("amyee_admin_token", tk);
      setToken(tk);
      toast.success("Welcome back.");
    } catch (err) {
      toast.error("Wrong password.");
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("amyee_admin_token");
    setToken("");
    setBookings([]);
  };

  const load = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await api.get("/admin/bookings", {
        headers: { "X-Admin-Token": token },
      });
      setBookings(res.data);
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error("Session expired. Login again.");
        logout();
      } else {
        toast.error("Failed to load bookings.");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (token) load();
    // load on token change
  }, [token]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#0A0E17] flex flex-col items-center justify-center px-4 grain relative">
        <Link to="/" className="absolute top-8 left-8 text-xs uppercase tracking-[0.3em] text-[#D4AF37] hover:text-white">
          ← Back to site
        </Link>
        <div className="relative w-full max-w-md bg-[#131B2A] border border-white/10 p-10">
          <div className="flex items-center gap-3 mb-8">
            <div className="w-10 h-10 border border-[#D4AF37]/40 flex items-center justify-center">
              <Lock className="w-4 h-4 text-[#D4AF37]" />
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Admin</p>
              <h1 className="font-serif text-2xl text-white">Amyee Travels</h1>
            </div>
          </div>

          <form onSubmit={login} className="space-y-6" data-testid="admin-login-form">
            <div>
              <Label className="text-[10px] uppercase tracking-[0.25em] text-gray-400 mb-1 block">
                Password
              </Label>
              <Input
                data-testid="admin-password-input"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="bg-transparent border-0 border-b border-white/20 rounded-none px-0 py-3 h-12 focus-visible:border-[#D4AF37] focus-visible:ring-0 focus-visible:ring-offset-0 text-white"
              />
            </div>
            <Button
              type="submit"
              data-testid="admin-login-submit"
              disabled={loading}
              className="w-full bg-[#D95D39] hover:bg-[#E27D60] text-white rounded-none py-6 h-auto uppercase tracking-wider"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>
          <p className="text-xs text-gray-500 mt-6 text-center">
            For Amyee Travels operations team only.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0E17] text-white">
      <header className="border-b border-white/10 bg-[#0A0E17]/90 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/" className="font-serif text-xl text-white">Amyee</Link>
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#D4AF37]">Admin · Bookings</span>
          </div>
          <div className="flex items-center gap-3">
            <Button
              onClick={load}
              data-testid="admin-refresh-button"
              variant="ghost"
              className="text-gray-300 hover:bg-white/5 hover:text-white rounded-none"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
            <Button
              onClick={logout}
              data-testid="admin-logout-button"
              variant="ghost"
              className="text-gray-300 hover:bg-white/5 hover:text-white rounded-none"
            >
              <LogOut className="w-4 h-4 mr-2" /> Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <div className="flex items-end justify-between mb-10">
          <div>
            <p className="text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-3">Inbox</p>
            <h2 className="font-serif font-light text-4xl md:text-5xl text-white tracking-tight">
              Booking requests
            </h2>
          </div>
          <Badge
            data-testid="admin-bookings-count"
            className="bg-[#D95D39]/20 text-[#D95D39] border border-[#D95D39]/40 rounded-none uppercase tracking-widest text-xs"
          >
            {bookings.length} {bookings.length === 1 ? "request" : "requests"}
          </Badge>
        </div>

        <div className="bg-[#131B2A] border border-white/10">
          <Table data-testid="admin-bookings-table">
            <TableHeader>
              <TableRow className="border-white/10 hover:bg-transparent">
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">When</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Name</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Phone</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Route</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Date</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Vehicle</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500">Notes</TableHead>
                <TableHead className="text-[10px] uppercase tracking-widest text-gray-500 text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {bookings.length === 0 && !loading && (
                <TableRow>
                  <TableCell colSpan={8} className="text-center text-gray-500 py-16">
                    No bookings yet.
                  </TableCell>
                </TableRow>
              )}
              {bookings.map((b) => (
                <TableRow key={b.id} className="border-white/10 hover:bg-white/[0.02]" data-testid={`booking-row-${b.id}`}>
                  <TableCell className="text-xs text-gray-400 whitespace-nowrap">
                    {new Date(b.created_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-white font-medium">{b.name}</TableCell>
                  <TableCell className="text-gray-300 font-mono">{b.phone}</TableCell>
                  <TableCell className="text-gray-300">
                    <span className="text-[#D4AF37]">{b.pickup}</span>
                    {b.drop && (
                      <>
                        <span className="mx-2 text-gray-600">→</span>
                        {b.drop}
                      </>
                    )}
                  </TableCell>
                  <TableCell className="text-gray-300">{b.travel_date}</TableCell>
                  <TableCell>
                    <Badge className="rounded-none bg-[#2C5F2D]/20 text-[#7FB47F] border border-[#2C5F2D]/40 uppercase tracking-wider text-[10px]">
                      {b.vehicle_type}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-gray-400 text-sm max-w-[220px] truncate">
                    {b.notes || "—"}
                  </TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <a
                      href={`tel:+91${b.phone}`}
                      data-testid={`booking-call-${b.id}`}
                      className="inline-flex items-center gap-1.5 text-[#D95D39] hover:text-[#E27D60] mr-3 text-sm"
                    >
                      <Phone className="w-3.5 h-3.5" /> Call
                    </a>
                    <a
                      href={`https://wa.me/91${b.phone}`}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`booking-whatsapp-${b.id}`}
                      className="inline-flex items-center gap-1.5 text-[#7FB47F] hover:text-white text-sm"
                    >
                      <MessageCircle className="w-3.5 h-3.5" /> WA
                    </a>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </main>
    </div>
  );
};

export default Admin;
